import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import BrandNav from '../../components/common/BrandNav';

export default function PriceListEditor() {
  const { brandId } = useParams();
  // Remember last visited brand for sidebar quick-navigation
  useEffect(() => { if (brandId) localStorage.setItem('pm_last_brand', brandId); }, [brandId]);
  const navigate    = useNavigate();

  const [brand,      setBrand]      = useState(null);
  const [categories, setCategories] = useState([]);
  const [items,      setItems]      = useState({});    // { catId: [item] }
  const [variants,   setVariants]   = useState({});    // { itemId: [variant] }
  const [loading,    setLoading]    = useState(true);
  const [pendingItems, setPendingItems] = useState({}); // { itemId: price }
  const [pendingVars,  setPendingVars]  = useState({}); // { variantId: price }

  const load = useCallback(async () => {
    try {
      const [bRes, catRes] = await Promise.all([
        api.get(`/brands/${brandId}`),
        api.get(`/categories?brand_id=${brandId}`),
      ]);
      setBrand(bRes.data);
      const cats = catRes.data;
      setCategories(cats);

      const itemMap    = {};
      const variantMap = {};
      await Promise.all(cats.map(async cat => {
        const iRes = await api.get(`/items?category_id=${cat.id}`);
        itemMap[cat.id] = iRes.data;
        await Promise.all(iRes.data.map(async item => {
          try {
            const vRes = await api.get(`/items/${item.id}/variants`);
            variantMap[item.id] = vRes.data;
          } catch { variantMap[item.id] = []; }
        }));
      }));
      setItems(itemMap);
      setVariants(variantMap);
    } catch { toast.error('Failed to load data'); }
    finally   { setLoading(false); }
  }, [brandId]);

  useEffect(() => { load(); }, [load]);

  // Save a single item price on blur
  const saveItemPrice = async (item, newPrice) => {
    const val = parseFloat(newPrice);
    if (isNaN(val) || String(val) === String(parseFloat(item.price))) return;
    try {
      await api.put(`/items/${item.id}`, { ...item, price: val });
      setItems(prev => {
        const next = { ...prev };
        for (const [cid, arr] of Object.entries(next)) {
          next[cid] = arr.map(i => i.id === item.id ? { ...i, price: val } : i);
        }
        return next;
      });
      toast.success('Price updated');
    } catch { toast.error('Save failed'); }
  };

  // Save a single variant price on blur
  const saveVariantPrice = async (itemId, variant, newPrice) => {
    const val = parseFloat(newPrice);
    if (isNaN(val) || String(val) === String(parseFloat(variant.price))) return;
    try {
      await api.put(`/items/${itemId}/variants/${variant.id}`, { ...variant, price: val });
      setVariants(prev => ({
        ...prev,
        [itemId]: (prev[itemId] || []).map(v => v.id === variant.id ? { ...v, price: val } : v),
      }));
      toast.success('Price updated');
    } catch { toast.error('Save failed'); }
  };

  if (loading) return <div className="spinner" style={{ marginTop: 60 }} />;

  const INPUT = {
    width: 88, padding: '5px 8px', borderRadius: 7,
    border: '1.5px solid #E0E0E0', fontSize: 13,
    textAlign: 'right', outline: 'none', fontFamily: 'inherit',
    transition: 'border-color 0.15s',
  };

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 24 }}>
        <div>
          <BrandNav brandId={brandId} subPath="price-list" backLabel="Menu Editor" backPath={`/brands/${brandId}/menu`} backKey="menu" />
          <h1 className="page-title">
            {brand?.name}
            <span style={{ fontWeight: 400, color: '#999', fontSize: 16, marginLeft: 8 }}>— Price List</span>
          </h1>
        </div>
      </div>

      {/* Column headers */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '6px 16px 6px 20px', marginBottom: 4 }}>
        <div style={{ flex: 1, fontSize: 11, fontWeight: 700, color: '#BDBDBD', textTransform: 'uppercase', letterSpacing: 0.8 }}>Item / Variant</div>
        <div style={{ width: 88, fontSize: 11, fontWeight: 700, color: '#BDBDBD', textTransform: 'uppercase', letterSpacing: 0.8, textAlign: 'right' }}>Price</div>
      </div>

      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E0E0E0', overflow: 'hidden' }}>
        {categories.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: '#bbb', fontSize: 14 }}>No categories found</div>
        )}

        {categories.map((cat, catIdx) => (
          <div key={cat.id}>
            {/* Category row — slide-only cats get a grey/dashed strip
                + a 📺 SLIDE chip beside the name (matches MenuEditor). */}
            <div style={{
              padding: '9px 16px',
              background: cat.slide_only ? '#EEF2F4' : '#F8F9FA',
              borderTop: catIdx === 0 ? 'none' : '2px solid #E8E8E8',
              borderBottom: cat.slide_only ? '1px dashed #B0BEC5' : '1px solid #E0E0E0',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: '#333' }}>{cat.name}</span>
              {cat.slide_only && (
                <span title="Slide-builder only — hidden from public QR menu"
                  style={{ fontSize: 9, background: '#CFD8DC', color: '#37474F',
                           borderRadius: 10, padding: '2px 6px', flexShrink: 0,
                           fontWeight: 700, letterSpacing: 0.3 }}>
                  📺 SLIDE
                </span>
              )}
              {cat.name_ar && <span style={{ fontSize: 12, color: '#aaa', fontFamily: 'Cairo' }}>{cat.name_ar}</span>}
              <span style={{ fontSize: 11, color: '#ccc', marginLeft: 'auto' }}>
                {(items[cat.id] || []).length} items
              </span>
            </div>

            {/* Items */}
            {(items[cat.id] || []).map((item, itemIdx) => {
              const itemVariants = variants[item.id] || [];
              return (
                <div key={item.id}
                  style={{ borderBottom: itemIdx < (items[cat.id]?.length ?? 0) - 1 ? '1px solid #F5F5F5' : 'none' }}>

                  {/* Item row */}
                  <div style={{ padding: '9px 16px 9px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    {/* Thumb — same fallback chain as MenuEditor: thumb → full image → icon
                        (items imported from skill backups often have image_path but no thumb yet) */}
                    {(item.image_thumb || item.image_path) ? (
                      <img src={item.image_thumb || item.image_path} alt="" style={{ width: 34, height: 34, borderRadius: 6, objectFit: 'contain', background: '#f5f5f5', flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: 34, height: 34, borderRadius: 6, background: '#F5F5F5', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
                        {item.icon || '🍽'}
                      </div>
                    )}

                    {/* Name */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.name}
                        {!item.is_available && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#E53935', background: '#FFEBEE', borderRadius: 4, padding: '1px 5px' }}>OFF</span>}
                      </div>
                      {item.name_ar && (
                        <div style={{ fontSize: 12, color: '#999', fontFamily: 'Cairo', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.name_ar}
                        </div>
                      )}
                    </div>

                    {/* Price input (only if no variants) */}
                    {itemVariants.length === 0 ? (
                      <input
                        type="number" step="0.01" min="0"
                        defaultValue={item.price || ''}
                        placeholder="0"
                        style={INPUT}
                        onFocus={e => e.currentTarget.style.borderColor = '#2196F3'}
                        onBlur={e => { e.currentTarget.style.borderColor = '#E0E0E0'; saveItemPrice(item, e.target.value); }}
                      />
                    ) : (
                      <div style={{ width: 88, textAlign: 'right', fontSize: 11, color: '#aaa' }}>— variants —</div>
                    )}
                  </div>

                  {/* Variant rows */}
                  {itemVariants.map(v => (
                    <div key={v.id} style={{ padding: '6px 16px 6px 70px', display: 'flex', alignItems: 'center', gap: 10, background: '#FAFAFA', borderTop: '1px solid #F0F0F0' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 13, color: '#555' }}>{v.name}</span>
                        {v.name_ar && <span style={{ fontSize: 11, color: '#aaa', fontFamily: 'Cairo', marginLeft: 6 }}>{v.name_ar}</span>}
                      </div>
                      <input
                        type="number" step="0.01" min="0"
                        defaultValue={v.price || ''}
                        placeholder="0"
                        style={INPUT}
                        onFocus={e => e.currentTarget.style.borderColor = '#2196F3'}
                        onBlur={e => { e.currentTarget.style.borderColor = '#E0E0E0'; saveVariantPrice(item.id, v, e.target.value); }}
                      />
                    </div>
                  ))}
                </div>
              );
            })}

            {(items[cat.id] || []).length === 0 && (
              <div style={{ padding: '14px 20px', fontSize: 13, color: '#ccc' }}>No items</div>
            )}
          </div>
        ))}
      </div>

      <div style={{ height: 40 }} />
    </div>
  );
}
